<template>
	<div class="report-page">
		<div
			v-if="filtersCategories.length"
			:class="['ui', 'top', 'attached', numbers[filtersCategories.length], 'item', 'menu']"
		>
			<a
				v-for="category in filtersCategories"
				:key="category.id"
				:class="[{ active: filtersCurrentCategory === category.id }, 'item', 'menu--item']"
				@click.prevent="() => updateCurrentCategory(category.id)"
			>{{ category.title }}</a>
		</div>
		<transition name="slide-fade">
			<div
				v-show="hasFilters && filtersViewVisible"
				ref="filtersView"
				class="dash-sidebar"
			>
				<filter-control
					ref="filtersContainer"
					:name="name"
					:values="filtersQueryValues"
					@submit="requestReport"
				/>
			</div>
		</transition>
		<div
			ref="reportView"
			class="dash-container"
		>
			<page-loader/>
			<div class="report-container">
				<div class="ui padded grid">
					<div class="row">
						<div class="eight wide column">
							<div class="ui form">
								<div class="inline fields">
									<div class="field">
										<div class="ui icon basic buttons" style="margin-right: 1rem">
											<button
												class="ui button"
												:data-tooltip="i18n('Action.goBackhome')"
												data-position="right center"
												@click="goBack()"
											>
												<i class="home icon"></i>
											</button>
										</div>
										<div class="ui icon basic buttons" style="margin-right: 1rem">
											<button
												v-show="hasFilters"
												class="ui button"
												:data-tooltip="i18n('Action.ToggleFilters')"
												data-position="right center"
												@click="toggleSidebar"
											>
												<i :class="['filter', { grey: !filtersViewVisible }, 'icon']"></i>
											</button>
										</div>
									</div>
									<div v-if="reports && Object.keys(reports).length > 1" class="field">
										<kendo-dropdown
											:dataSource="subReportsDataSource"
											:value="name"
											:index="1"
											style="width: 200px;"
											@change="changeRoute"
										/>
									</div>
									<div v-if="subReportPages.length > 1" class="field">
										<kendo-dropdown
											:dataSource="subReportPages"
											:value="subReportCurrentPage"
											style="width: 200px;"
											@change="target => updateCurrentPage(target.value)"
										/>
									</div>
								</div>
							</div>
						</div>
						<div class="right aligned eight wide column">
							<notifications-button/>
							<div
								v-if="!hidePrint"
								class="ui icon basic buttons"
								style="margin-right: 1rem"
							>
								<button
									class="ui button"
									:data-tooltip="i18n('Action.ExportToExcel')"
									data-position="bottom center"
									@click.prevent="exportToExcel"
								>
									<i class="file excel outline icon"></i>
								</button>
								<button
									class="ui button"
									:data-tooltip="i18n('Action.SaveAsPDF')"
									data-position="bottom center"
									@click.prevent="saveAsPDF"
								>
									<i class="file pdf outline icon"></i>
								</button>
							</div>
							<div
								v-if="!hidePrint && isDetail"
								class="ui icon basic buttons"
								style="margin-right: 1rem"
							>
								<button
									v-show="hasExportDetail"
									name="btn-export-detail-excel"
									class="ui button"
									:data-tooltip="i18n('Action.ExportToExcel')"
									data-position="bottom center"
									@click.prevent="exportDetailToExcel"
								>
									<i class="file excel outline icon"></i>
								</button>
							</div>

							<div v-if="!hidePrint" class="ui icon basic buttons" style="margin-right: 1rem">
								<button
									:class="['ui', { active: printMode }, 'button']"
									:data-tooltip="i18n('Action.Print')"
									data-position="bottom center"
									@click.prevent="sendToPrint"
								>
									<i class="print icon"></i>
								</button>
							</div>
							<div class="ui icon basic buttons">
								<a
									href="mailto:suportegig@neocom.info"
									class="ui button"
									data-tooltip="suportegig@neocom.info"
									data-position="left center"
								>
									<i class="mail icon"></i>
								</a>
							</div>
						</div>
					</div>
					<div v-if="!reports || !Object.keys(reports).length" class="row">
						<div class="sixteen wide column">
							<div class="ui container">
								<div class="ui icon message">
									<i class="area chart icon"></i>
									<div class="content">
										<div class="header">{{ i18n('Message.WelcomeDashboard') }}</div>
										<p>{{ i18n('Message.WelcomeDashboardMessage') }}</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<component
					v-if="isAvailable"
					:is="$route.params.reportName"
					:name="name"
					ref="reportContainer"
				></component>
				<report-unavailable v-else-if="isValid"/>
				<report-swap :dataSource="subReportsDataSource" :name="name"/>
			</div>
		</div>
	</div>
</template>

<script>
  import _ from 'lodash';
  import {mapActions, mapGetters} from 'vuex';

  import FilterControl from '@/modules/neodash/components/filter-control/filter-control';
  import KendoDropdown from '@/components/kendo-dropdown/kendo-dropdown';
  import NotificationsButton from '@/components/notifications-button/notifications-button';
  import PageLoader from '@/components/page-loader/page-loader.vue';
  import ReportSwap from '@/modules/neodash/components/report-swap/report-swap';
  import ReportUnavailable from '@/modules/neodash/components/report-unavailable/report-unavailable';

  import CustomersFrequency from '@/modules/neodash/containers/customers-frequency/customers-frequency';
  import Registration from '@/modules/neodash/containers/registration/registration';
  import MarketBreakDown from '@/modules/neodash/containers/market-break-down/market-break-down';

  import RetailBilling from '@/modules/neodash/containers/retail-billing/retail-billing';

  import MarketAnalysis from '@/modules/neodash/containers/market-analysis/market-analysis';
  import Rap from '@/modules/neodash/containers/rap/rap';
  import RegistrationSegmentRegion
    from '@/modules/neodash/containers/registration-segment-region/registration-segment-region';
  import RetailSalesPerformance from '@/modules/neodash/containers/retail-sales-performance/retail-sales-performance';

  import {reportStatus} from '@/modules/neodash/enuns/report.enum';
  import {settingsViewMode} from '@/modules/neodash/enuns/settings.enun';
  import {httpMixin, i18nMixin, momentMixin, numeralMixin, tableMixin} from '@/modules/neodash/mixins';

  import {AUTH_GET_TOKEN, AUTH_GET_USERNAME} from '@/store/modules/authentication/types';
  import {CONFIG} from '@/config';
  import {
    FILTERS_GET_CATEGORIES,
    FILTERS_GET_CURRENT_CATEGORY,
    FILTERS_GET_DATA,
    FILTERS_GET_ID,
    FILTERS_GET_VALUES,
    FILTERS_UPDATE_CURRENT_CATEGORY,
    FILTERS_UPDATE_DATA,
    FILTERS_GET_EXPORT
  } from '@/modules/neodash/store/modules/filters/types';
  import {PRINT_GET_PRINT_MODE, PRINT_TOGGLE_TABLE_MODE} from '@/store/modules/print/types';
  import {
    REPORT_GET_REPORTS,
    REPORT_GET_STATE,
    REPORT_GET_SUBREPORT_CURRENT_PAGE,
    REPORT_GET_SUBREPORT_PAGES,
    REPORT_GET_TITLE,
    REPORT_UPDATE_DATA,
    REPORT_UPDATE_SUBREPORT_CURRENT_PAGE
  } from '@/modules/neodash/store/modules/report/types';

  import {SETTINGS_GET_VIEW_MODE, SETTINGS_TOGGLE_VIEW_MODE} from '@/store/modules/settings/types';
  import {AnalysisBreakDownCityDetail} from '../../services/analysis-break-down/analysis-break-down-city-detail';
  import {AnalysisBreakDownPmaDetail} from '../../services/analysis-break-down/analysis-break-down-pma-detail';
  import {SpreadsheetComposer} from '../../services/spreadsheet-composer';

  export default {
    props: {
      reportName: {
        type: String,
        required: true
      },

      subReportName: {
        type: String
      }
    },

    data() {
      return {
        filtersViewVisible: true,
        numbers: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
        settingsViewMode,
        sidebarSize: 210,
        subReportPage: undefined,
        reportMetadata: []
      };
    },

    computed: {
      ...mapGetters({
        filtersValues: FILTERS_GET_VALUES,
        filtersData: FILTERS_GET_DATA,
        filtersCategories: FILTERS_GET_CATEGORIES,
        filtersCurrentCategory: FILTERS_GET_CURRENT_CATEGORY,
        currentFilterId: FILTERS_GET_ID,
        printMode: PRINT_GET_PRINT_MODE,
        reportState: REPORT_GET_STATE,
        reports: REPORT_GET_REPORTS,
        subReportCurrentPage: REPORT_GET_SUBREPORT_CURRENT_PAGE,
        subReportPages: REPORT_GET_SUBREPORT_PAGES,
        title: REPORT_GET_TITLE,
        viewMode: SETTINGS_GET_VIEW_MODE,
        userName: AUTH_GET_USERNAME,
        userToken: AUTH_GET_TOKEN,
        filtersIsExportDetail: FILTERS_GET_EXPORT
      }),

      hasFilters() {
        const res = _.size(this.filtersData) > 0;
        this.sidebarSize = res ? 210 : 0;
        return res;
      },
      hasExportDetail() {
        return this.filtersIsExportDetail;
      },
      subReportsDataSource() {
        return _(this.reports)
            .filter(r => (_.has(r, 'isActive') ? r.isActive : true))
            .filter(r => (_.has(r, 'status') ? r.status !== reportStatus.unavailable : true))
            .map(r => ({text: r.title, value: r.id}))
            .orderBy(r => r.text)
            .value();
      },

      name() {
        return this.subReportName || (this.reports[Object.keys(this.reports)[0]] || {id: this.reportName}).id;
      },

      filtersQueryValues() {
        return this.$route.query && this.$route.query.filters
            ? _.reduce(
                JSON.parse(this.$route.query.filters),
                (acc, val, key) => {
                  acc[key] = {text: val, value: val};
                  return acc;
                },
                {}
            )
            : undefined;
      },

      isValid() {
        return this.reports && _.size(this.reports);
      },

      isAvailable() {
        if (!this.isValid) {
          return false;
        }

        if (_.has(this.reports[this.name], 'status')) {
          return this.reports[this.name].status !== reportStatus.unavailable;
        }

        return true;
      },

      hidePrint() {
        switch (this.reportName) {
          case 'registration-segment-region':
            return true;
          case 'retail-sales-performance':
            return true;
          default:
            return false;
        }
      },

      isDetail() {
        switch (this.reportName) {
          case 'market-break-down':
            return true;
          default:
            return false;
        }
      },

      referenceDate() {
        if (this.filtersValues && this.filtersValues.ano && this.filtersValues.mes) {
          return `${this.filtersValues.ano.value}${
              this.filtersValues.mes.value < 10 ? `0${this.filtersValues.mes.value}` : this.filtersValues.mes.value
          }`;
        }
        return null;
      }
    },

    mixins: [httpMixin, i18nMixin, numeralMixin, momentMixin, tableMixin],

    methods: {
      ...mapActions({
        toggleViewMode: SETTINGS_TOGGLE_VIEW_MODE,
        updateReportData: REPORT_UPDATE_DATA,
        updateCurrentCategory: FILTERS_UPDATE_CURRENT_CATEGORY,
        updateCurrentPage: REPORT_UPDATE_SUBREPORT_CURRENT_PAGE
      }),

      goBack() {
        this.$router.push({
          name: 'home'
        });
      },

      changeRoute(path) {
        this.$router.push({
          name: 'report-page',
          params: {
            reportName: this.reportName,
            subReportName: path.value
          }
        });
      },

      requestReport() {
        const req = {
          id: this.reportName
        };
        _.forEach(this.filtersQueryValues ? this.filtersQueryValues : this.filtersValues, (val, key) => {
          req[key] = val.value;
        });

        this.http.post(`${CONFIG.URL.NEODASH_API}/api/dashboard/report/${req.id}`, req)
            .then(res => {
              this.updateReportData({...res.data, id: this.reportName});

              this.requestFilters();
            })
            .catch(err => {
              console.log(err);
            });
      },

      requestFilters() {
        this.http
            .get(`${CONFIG.URL.NEODASH_API}/api/dashboard/report/${this.reportName}/filters`)
            .then(res => {
              this.$store.dispatch(FILTERS_UPDATE_DATA, {...res.data, id: this.reportName});
            })
            .catch(error => error);
      },

      toggleSidebar() {
        if (this.filtersViewVisible) {
          this.$refs.reportView.animate({left: [210 + 'px', 0]}, {duration: 300});
          this.sidebarSize = 0;
        } else {
          this.$refs.reportView.animate({left: [0, 210 + 'px']}, {duration: 300});
          this.sidebarSize = 210;
        }
        this.filtersViewVisible = !this.filtersViewVisible;
      },

      saveAsPDF() {
        const filters = JSON.stringify(
            _.reduce(
                this.filtersValues,
                (acc, val, key) => {
                  acc[key] = {value: val.value, text: val.text};
                  return acc;
                },
                {}
            )
        );

        this.httpFile
            .post(`${CONFIG.URL.NEODASH_API}/api/report/pdf`, {
              url: `${CONFIG.URL.HOST}/report/${this.reportName}/printing?reports=${JSON.stringify([
                this.name
              ])}&filters=${filters}&user=${this.userName}&apikey=${this.userToken}`
            })
            .then(res => {
              const blob = new Blob([res.data], {
                type: 'application/pdf'
              });

              return window.URL.createObjectURL(blob);
            })
            .then(file =>
                kendo.saveAs({
                  dataURI: file,
                  fileName: `${_.snakeCase(_.deburr(this.title))}-${_.snakeCase(
                      _.deburr(this.reports[this.name].title)
                  )}-${this.moment().format('YYYYMMDD-HHmmss')}.pdf`
                })
            )
            .catch(err => console.log(err));
      },

      exportToExcel() {
        this.$store.dispatch(PRINT_TOGGLE_TABLE_MODE);
        setTimeout(() => {
          console.log(this.title, this.reports);
          const title = this.title;
          const subtitle = this.reports[this.name] ? this.reports[this.name].title : this.title;
          this.exportHtmlToExcel(
              this.$refs.reportContainer.$el.innerHTML,
              `${_.snakeCase(_.deburr(title))}-${_.snakeCase(_.deburr(subtitle))}-${this.moment().format(
                  'YYYYMMDD-HHmmss'
              )}.xls`
          );
          this.$store.dispatch(PRINT_TOGGLE_TABLE_MODE);
        }, 300);
      },
      serializeQueryString(obj, prefix) {
        var str = [],
            p;
        for (p in obj) {
          if (obj.hasOwnProperty(p)) {
            var k = prefix ? prefix + '[' + p + ']' : p,
                v = obj[p];
            str.push(
                v !== null && typeof v === 'object' ? serialize(v, k) : encodeURIComponent(k) + '=' + encodeURIComponent(v)
            );
          }
        }
        return str.join('&');
      },
      exportDetailToExcel() {
        if (_.isUndefined(this.filtersValues.ano_fim)) return;
        const requestData = {
          EndYear: this.filtersValues.ano_fim.value,
          EndMonth: this.filtersValues.mes_final.value,
          StartYear: this.filtersValues.ano_inicio.value,
          StartMonth: this.filtersValues.mes_inicial.value,
          TypeLocality: this.filtersValues.tipo_localidade.value
        };

        this.http
            .get(
                `${
                    CONFIG.URL.NEODASH_API
                }/api/gigdash/marketBreakDown/getAnalysisMktBreakDownDetailed?${this.serializeQueryString(requestData)}`
            )
            .then(res => {
              if (_.size(res) > 0)
                setTimeout(() => {
                  let spreadSheetComposer = new SpreadsheetComposer(`_Market_Break_Down_detail`);
                  let cities = new AnalysisBreakDownCityDetail().mount(res.data.exportCity);
                  let pmas = new AnalysisBreakDownPmaDetail().mount(res.data.exportPma);

                  spreadSheetComposer
                      .mount([
                        !_.isUndefined(cities) ? cities.workbookOptions.sheets : [],
                        !_.isUndefined(pmas) ? pmas.workbookOptions.sheets : []
                      ])
                      .create()
                      .saveAsExcel();
                }, 300);
              else
                notifyError(null, {
                  type: NOTIFY_TYPE.ERROR,
                  text: 'Não foi possível realizar a exportação dos dados.'
                });
            });
      },

      sendToPrint() {
        const filters = JSON.stringify(
            _.reduce(
                this.filtersValues,
                (acc, val, key) => {
                  acc[key] = {value: val.value, text: val.text};
                  return acc;
                }, {}
            )
        );

        window.open(
            `/neodash/report/${this.reportName}/printing?dialog=1&reports=${JSON.stringify([
              this.name
            ])}&filters=${filters}&user=${this.userName}&apikey=${this.userToken}`
        );
      },

      init() {
        if (_.size(this.filtersValues) || this.filtersQueryValues) {
          this.requestReport();
        } else {
          this.requestFilters();
        }
      }
    },

    created() {
      this.init();
    },

    watch: {
      reportName() {
        this.init();
      }
    },

    components: {
      FilterControl,
      KendoDropdown,
      NotificationsButton,
      PageLoader,
      ReportSwap,
      ReportUnavailable,
      CustomersFrequency,
      Rap,
      Registration,
      MarketAnalysis,
      RetailBilling,
      MarketBreakDown,
      RegistrationSegmentRegion,
      RetailSalesPerformance
    }
  };
</script>

<style lang="scss" scoped>
	@import "../../../../main";

	$widthSideBar: 220px;
	$heightContainers: calc(100vh - #{$heightNavBar});

	.report-page {
		width: 100vw;
		display: flex;
		flex-wrap: wrap;

		.dash-sidebar {
			overflow-y: auto;
			width: $widthSideBar;
			height: $heightContainers;
			padding: 20px;
			background-color: #f1f1f1;
			border-right: 1px solid #cccccc;
			box-shadow: 1px 0 0 #fff;
			z-index: 1;

			.k-widget.k-tooltip {
				width: 93%;
			}
		}

		.dash-container {
			overflow-y: auto;
			padding: 20px;
			background-color: #f1f1f1;
			width: calc(100vw - #{$widthSideBar});
			height: $heightContainers;
		}
	}

	.ui.basic.button,
	.ui.basic.buttons {
		background-color: #ffffff !important;
		box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.2), 0 1px 2px rgba(0, 0, 0, 0.05) !important;
		border: 1px solid #ccc !important;
	}

	.hide {
		display: none !important;
	}

	.show {
		display: block !important;
	}
</style>
